.progress {
  display: flex;
  align-items: center;
  gap: 0.6em;
  min-width: 0;
  color: var(--status-warning);
}
.visual {
  position: relative;
  flex: 0 0 auto;
}
.timing {
  margin: 0;
  color: var(--ink-secondary);
  font-size: 0.72em;
}
.overdue {
  color: var(--status-serious);
}
.overdue .timing {
  color: var(--status-serious);
}
.readout {
  width: 12ch;
  flex: 0 0 12ch;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  text-align: end;
  white-space: nowrap;
}
.radial .visual {
  width: 2.35em;
  height: 2.35em;
  border: 0.14em solid var(--edge);
  border-radius: 50%;
  background: conic-gradient(var(--status-warning) var(--running-progress), var(--edge) 0);
  box-shadow:
    inset 0 0 0 0.22em var(--surface-raised),
    0 0 0.55em color-mix(in srgb, var(--status-warning) 45%, transparent);
}
.radial .visual::after {
  content: "";
  position: absolute;
  inset: -0.14em;
  border-radius: 50%;
  border-top: 0.14em solid var(--status-warning);
  animation: sweep 1.7s linear infinite;
}
.overdue.radial .visual::before {
  content: "";
  position: absolute;
  inset: -0.42em;
  border: 0.1em solid var(--status-serious);
  border-radius: 50%;
  box-shadow: 0 0 0.55em color-mix(in srgb, var(--status-serious) 65%, transparent);
  animation: alarm-ring 1.8s ease-in-out infinite;
}
.radialCore {
  position: absolute;
  inset: 0.68em;
  border-radius: 50%;
  background: var(--status-warning);
  box-shadow: 0 0 0.45em var(--status-warning);
}
.overdue .radialCore {
  background: var(--status-serious);
  box-shadow: 0 0 0.55em var(--status-serious);
  animation: reactor-breathe 1.8s ease-in-out infinite alternate;
}
.runway {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-self: stretch;
  container-type: inline-size;
}
.runway .visual {
  width: min(12em, 100%);
  max-width: 100%;
  height: 0.8em;
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 999px;
  background: linear-gradient(90deg, var(--status-warning) var(--running-progress), var(--edge) 0);
}
.runway .visual::after {
  content: "";
  position: absolute;
  top: -0.25em;
  bottom: -0.25em;
  left: calc(var(--running-progress) - 0.06em);
  width: 0.12em;
  background: var(--ink-primary);
  box-shadow: 0 0 0.45em var(--ink-primary);
}
.overdue.runway .visual::before {
  content: "";
  position: absolute;
  inset: -0.2em 0;
  left: calc(100% - 0.55em);
  width: 0.55em;
  background: repeating-linear-gradient(
    -45deg,
    var(--status-serious) 0 0.12em,
    var(--surface) 0.12em 0.24em
  );
  box-shadow: 0 0 0.5em color-mix(in srgb, var(--status-serious) 70%, transparent);
  animation: barrier-pulse 1.4s ease-in-out infinite alternate;
}
.overdue.runway .visual::after {
  background: var(--status-serious);
  box-shadow: 0 0 0.55em var(--status-serious);
  animation: barrier-pulse 1.4s ease-in-out infinite alternate;
}
.spark {
  position: absolute;
  top: 50%;
  left: -0.5em;
  width: 0.35em;
  height: 0.35em;
  border-radius: 50%;
  background: var(--ink-primary);
  box-shadow: 0 0 0.4em var(--status-warning);
  animation: spark 1.5s linear infinite;
}
.overdue.runway .spark {
  animation-duration: 1s;
}
.sparkTwo {
  display: none;
  animation-delay: -0.5s;
}
.overdue.runway .sparkTwo {
  display: block;
}
.orbit .visual {
  width: 2.4em;
  height: 2.4em;
  border: 1px solid var(--edge);
  border-radius: 50%;
}
/* biome-ignore lint/style/noDescendingSpecificity: the radial overdue ring precedes this separate legacy treatment. */
.orbit .visual::before {
  content: "";
  position: absolute;
  inset: 0.18em;
  border: 0.12em solid transparent;
  border-top-color: var(--status-warning);
  border-radius: 50%;
  transform: rotate(var(--running-degrees));
}
.orbitCore {
  position: absolute;
  inset: 0.75em;
  border-radius: 50%;
  background: var(--status-warning);
  box-shadow: 0 0 0.45em var(--status-warning);
}
.overdue .orbitCore {
  background: var(--status-serious);
  box-shadow: 0 0 0.55em var(--status-serious);
  animation: reactor-breathe 1.15s ease-in-out infinite alternate;
}
.particle {
  position: absolute;
  top: 0.25em;
  left: calc(50% - 0.16em);
  width: 0.32em;
  height: 0.32em;
  border-radius: 50%;
  background: var(--ink-primary);
  transform-origin: 0.16em 0.95em;
  animation: orbit 1.8s linear infinite;
}
.particleTwo {
  animation-delay: -0.9s;
  background: var(--status-warning);
}
.overdue.orbit .visual::after {
  content: "";
  position: absolute;
  inset: 0.42em;
  border: 0.08em solid color-mix(in srgb, var(--status-serious) 75%, transparent);
  border-radius: 50%;
  box-shadow: 0 0 0.6em color-mix(in srgb, var(--status-serious) 55%, transparent);
  animation: gravity-well 1.15s ease-in-out infinite alternate;
}
.overdue.orbit .particle {
  transform-origin: 0.16em 0.7em;
  animation-duration: 0.95s;
}
.indeterminate .visual {
  background-image: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--status-warning) 70%, transparent) 0 0.25em,
    var(--edge) 0.25em 0.5em
  );
  animation: shift 1s linear infinite;
}
.signalField {
  container-type: size;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-self: stretch;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
}
.signalField .visual {
  width: auto;
  height: 1em;
  max-height: 100%;
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 999px;
  background: var(--surface);
}
.rail {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, var(--status-warning) var(--running-progress), var(--edge) 0);
  opacity: 0.75;
}
.lead {
  position: absolute;
  top: -0.2em;
  bottom: -0.2em;
  left: calc(var(--running-progress) - 0.08em);
  width: 0.16em;
  border-radius: 999px;
  background: var(--ink-primary);
  box-shadow: 0 0 0.5em var(--status-warning);
}
.pulse {
  position: absolute;
  top: 50%;
  left: -0.5em;
  width: 0.3em;
  height: 0.3em;
  border-radius: 50%;
  background: var(--ink-primary);
  box-shadow: 0 0 0.4em var(--status-warning);
  animation: pulse 2.4s ease-in-out infinite alternate;
}
.pulseTwo {
  animation-delay: -1.2s;
}
.tracks {
  display: none;
}
@container panel (min-width: 26rem) {
  .runway .visual {
    flex: 1 1 auto;
    width: auto;
    height: clamp(1.1em, 7cqw, 2.7em);
    box-shadow: inset 0 0 1em color-mix(in srgb, var(--status-warning) 16%, transparent);
  }
  .runway .visual::after {
    left: calc(var(--running-progress) - 0.08em);
    width: 0.16em;
  }
  .spark {
    animation-name: sparkWide;
  }
}
@container panel (max-width: 18rem) {
  .signalField {
    flex: 0 1 auto;
    min-height: 0;
  }
  .signalField .visual {
    width: 2.65em;
    height: 1.15em;
  }
  .signalField .rail {
    background: var(--edge);
  }
  .signalField .lead {
    top: 0.2em;
    bottom: 0.2em;
    width: 0.38em;
    border-radius: 50%;
  }
  .pulse {
    display: none;
  }
}
@container panel (max-height: 5rem) {
  .signalField {
    flex: 0 1 auto;
    min-height: 0;
  }
  .signalField .visual {
    width: 2.65em;
    height: 1.15em;
  }
  .signalField .rail {
    background: var(--edge);
  }
  .signalField .lead {
    top: 0.2em;
    bottom: 0.2em;
    width: 0.38em;
    border-radius: 50%;
  }
  .pulse {
    display: none;
  }
}
/* A size-contained signal field cannot use its own intrinsic height as a query input: its
   auto-sized content would always satisfy the compact branch. Width is the stable panel-scale
   discriminator; the field's parent already controls its available height. */
@container panel (min-width: 26rem) {
  .signalField .visual {
    height: 100%;
    min-height: 0;
    border-radius: 0.25em;
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--edge) 60%, transparent),
      transparent
    );
  }
  .signalField .visual::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: var(--running-progress);
    background:
      repeating-linear-gradient(
        0deg,
        transparent 0 0.55em,
        color-mix(in srgb, var(--status-warning) 16%, transparent) 0.55em 0.6em
      ),
      linear-gradient(
        90deg,
        color-mix(in srgb, var(--status-warning) 19%, transparent),
        color-mix(in srgb, var(--status-warning) 7%, transparent)
      );
    box-shadow: inset -0.8em 0 1em color-mix(in srgb, var(--status-warning) 22%, transparent);
  }
  .signalField .rail {
    top: 50%;
    bottom: auto;
    height: 1px;
  }
  .signalField .lead {
    top: calc(50% - 0.45em);
    bottom: auto;
    height: 0.9em;
  }
  .pulse {
    display: none;
  }
  .tracks {
    position: absolute;
    inset: 0.8em 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .track {
    position: relative;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      color-mix(in srgb, var(--edge) 85%, transparent),
      transparent
    );
  }
  .markerAnchor {
    width: 0.38em;
    height: 0.22em;
  }
  .markerBody {
    border-radius: 0.06em;
    background: var(--status-warning);
    box-shadow: 0 0 0.4em var(--status-warning);
  }
  .track:nth-child(2) .markerBody {
    animation-delay: -0.8s;
    opacity: 0.7;
  }
  .track:nth-child(3) .markerBody {
    animation-delay: -1.7s;
    opacity: 0.45;
  }
  .track:nth-child(4) .markerBody {
    animation-delay: -2.4s;
    opacity: 0.7;
  }
  .track:nth-child(5) .markerBody {
    animation-delay: -0.35s;
    opacity: 0.55;
  }
}
.overdue.signalField .rail {
  background: linear-gradient(90deg, var(--status-serious) 0 100%);
  opacity: 0.42;
}
.overdue.signalField .lead {
  background: var(--status-serious);
  box-shadow: 0 0 0.7em var(--status-serious);
  animation: endpoint-pulse 1.2s ease-in-out infinite alternate;
}
.overdue.signalField .markerBody {
  --phased-marker-travel: -10em;
  background: var(--status-serious);
  box-shadow: 0 0 0.55em var(--status-serious);
  animation-duration: 0.68s;
  animation-timing-function: ease-in-out;
}
.short .signalField .visual {
  height: 1em;
}
@keyframes sweep {
  to {
    transform: rotate(360deg);
  }
}
@keyframes alarm-ring {
  from {
    transform: scale(0.92);
    opacity: 0.45;
  }
  to {
    transform: scale(1.08);
    opacity: 1;
  }
}
@keyframes reactor-breathe {
  from {
    transform: scale(0.84);
    opacity: 0.7;
  }
  to {
    transform: scale(1.12);
    opacity: 1;
  }
}
@keyframes barrier-pulse {
  from {
    opacity: 0.55;
  }
  to {
    opacity: 1;
  }
}
@keyframes gravity-well {
  from {
    transform: scale(0.82);
    opacity: 0.45;
  }
  to {
    transform: scale(1.08);
    opacity: 0.95;
  }
}
@keyframes endpoint-pulse {
  from {
    transform: scaleY(0.75);
    opacity: 0.65;
  }
  to {
    transform: scaleY(1.15);
    opacity: 1;
  }
}
@keyframes spark {
  to {
    transform: translateX(13em);
  }
}
@keyframes sparkWide {
  to {
    transform: translateX(100cqw);
  }
}
@keyframes orbit {
  to {
    transform: rotate(360deg);
  }
}
@keyframes shift {
  to {
    background-position: 1em 0;
  }
}
@keyframes pulse {
  from {
    transform: translate(-0.5em, -50%);
  }
  to {
    transform: translate(calc(var(--running-progress) + 0.5em), -50%);
  }
}
@media (prefers-reduced-motion: reduce) {
  .radial .visual::after,
  .overdue.radial .visual::before,
  .overdue .radialCore,
  .spark,
  .runway .visual::after,
  .overdue.runway .visual::before,
  .overdue.runway .visual::after,
  .particle,
  .overdue.orbit .visual::after,
  .pulse,
  .lead,
  .overdue.signalField .lead,
  .overdue.signalField .markerBody,
  /* biome-ignore lint/style/noDescendingSpecificity: the shared reduced-motion list intentionally follows themed overrides. */
  .signalField .visual::before,
  .indeterminate .visual {
    animation: none;
  }
}
